import { TFilterOptions } from '@evinced-private/ui-common';

import omit from 'lodash-es/omit';
import { CUSTOM_FIELDS_FILTER_PARAM_KEY } from 'src/apps/dashboard/constants/CustomFields';
import {
	ANDROID_OS_VALUES,
	IOS_OS_VALUES,
	MOBILE_APP_ID_FILTER_ID,
	MOBILE_PLATFORM_FILTER_ID,
	MobilePlatform,
	parseMobilePlatform
} from 'src/apps/dashboard/constants/MobileDashboard.constants';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import { LabelsExcludedFromDashboard } from 'src/common/types/ConfigurationTypes';
import {
	ConditionOperators,
	ConditionString,
	ConditionStringArray
} from 'src/common/types/graphql.generated';

interface IMobileRunFilters {
	mfaOsFilter?: ConditionStringArray;
	mfaAppIdFilter?: ConditionStringArray;
	msdkOsFilter?: ConditionString;
	msdkAppFilter?: ConditionString;
}

const MOBILE_FILTER_KEYS = [
	'dateRange',
	'searchValue',
	MOBILE_PLATFORM_FILTER_ID,
	MOBILE_APP_ID_FILTER_ID,
	'unitId'
];

const getPlatformFromFilters = (filters?: IFilterOptions): MobilePlatform =>
	parseMobilePlatform(filters?.[MOBILE_PLATFORM_FILTER_ID]?.[0]?.value);

const buildMfaOsFilter = (platform: MobilePlatform): ConditionStringArray | undefined => {
	if (platform === 'all') {
		return undefined;
	}

	const values = platform === 'ios' ? IOS_OS_VALUES : ANDROID_OS_VALUES;
	return {
		value: [values],
		operator: ConditionOperators.Any
	};
};

const buildMsdkOsFilter = (platform: MobilePlatform): ConditionString | undefined => {
	if (platform === 'all') {
		return undefined;
	}

	const value = platform === 'ios' ? IOS_OS_VALUES[0] : ANDROID_OS_VALUES[0];
	return { value: [value] };
};

export const getMobileAppIdFromFilters = (filters?: IFilterOptions): string | undefined => {
	const appIdFilter = filters?.[MOBILE_APP_ID_FILTER_ID];

	if (!appIdFilter) {
		return undefined;
	}

	if (typeof appIdFilter === 'string') {
		const trimmed = appIdFilter.trim();
		return trimmed || undefined;
	}

	if (Array.isArray(appIdFilter) && appIdFilter.length > 0) {
		const appId = appIdFilter[0]?.value;
		return appId ? String(appId) : undefined;
	}

	return undefined;
};

const buildMfaAppIdFilter = (filters?: IFilterOptions): ConditionStringArray | undefined => {
	const appId = getMobileAppIdFromFilters(filters);
	if (!appId) {
		return undefined;
	}

	return {
		value: [[appId]],
		operator: ConditionOperators.All
	};
};

const buildMsdkAppFilter = (filters?: IFilterOptions): ConditionString | undefined => {
	const appId = getMobileAppIdFromFilters(filters);
	if (!appId) {
		return undefined;
	}

	return { value: [appId] };
};

export const buildMobileRunFilters = (filters?: IFilterOptions): IMobileRunFilters => {
	const platform = getPlatformFromFilters(filters);

	return {
		mfaOsFilter: buildMfaOsFilter(platform),
		mfaAppIdFilter: buildMfaAppIdFilter(filters),
		msdkOsFilter: buildMsdkOsFilter(platform),
		msdkAppFilter: buildMsdkAppFilter(filters)
	};
};

const buildMobileCustomFieldsVariables = (
	filters?: IFilterOptions,
	labelsExcludedFromDashboard?: LabelsExcludedFromDashboard
): {
	customFieldsFilter?: ConditionStringArray;
	customFieldsFilterExcludes?: ConditionStringArray;
} => {
	const variablesBuilder = new QueryVariablesBuilder<Record<string, unknown>>();

	if (filters) {
		const customFieldOptions = omit(filters, MOBILE_FILTER_KEYS) as TFilterOptions;

		variablesBuilder.addTupleFilterParam(
			CUSTOM_FIELDS_FILTER_PARAM_KEY,
			customFieldOptions,
			ConditionOperators.All,
			true
		);
	}

	if (labelsExcludedFromDashboard && Object.keys(labelsExcludedFromDashboard).length > 0) {
		variablesBuilder.addNeTupleFilterParam(
			'customFieldsFilterExcludes',
			labelsExcludedFromDashboard
		);
	}

	const variables = variablesBuilder.getVariables();

	return {
		customFieldsFilter: variables.customFieldsFilter as ConditionStringArray | undefined,
		customFieldsFilterExcludes: variables.customFieldsFilterExcludes as
			| ConditionStringArray
			| undefined
	};
};

export const buildMobileBaseVariables = (
	filters?: IFilterOptions,
	labelsExcludedFromDashboard?: LabelsExcludedFromDashboard
): IMobileRunFilters & {
	customFieldsFilter?: ConditionStringArray;
	customFieldsFilterExcludes?: ConditionStringArray;
} => ({
	...buildMobileRunFilters(filters),
	...buildMobileCustomFieldsVariables(filters, labelsExcludedFromDashboard)
});
